import React, { useEffect, useMemo, useState } from 'react';
import './WithdrawalDetail.css';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import api from '../../api';
import backIcon from '../../assets/backspace-icon.png';

export default function WithdrawalDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { search } = useLocation();

  const network = useMemo(() => {
    const params = new URLSearchParams(search);
    const selectedNetwork = (params.get('network') || '').toLowerCase();

    return selectedNetwork === 'testnet' || selectedNetwork === 'mainnet'
      ? selectedNetwork
      : 'mainnet';
  }, [search]);

  const [withdrawal, setWithdrawal] = useState(null);
  const [confirmationAddress, setConfirmationAddress] = useState('');
  const [copied, setCopied] = useState(false);
  const [processingAction, setProcessingAction] = useState('');

  useEffect(() => {
    let cancelled = false;

    const fetchWithdrawal = async () => {
      try {
        const { data } = await api.get(
          `/withdrawals/${id}?network=${network}`
        );

        if (!cancelled) {
          setWithdrawal(data.withdrawal);
        }
      } catch (err) {
        console.error('❌ Failed to load withdrawal detail:', err);

        if (!cancelled) {
          alert(
            err?.response?.data?.message ||
              'Failed to load the withdrawal request.'
          );
        }
      }
    };

    fetchWithdrawal();

    return () => {
      cancelled = true;
    };
  }, [id, network]);

  const handleApprove = async () => {
    if (processingAction || !withdrawal) return;

    const enteredAddress = confirmationAddress.trim();
    const destinationAddress = withdrawal.walletAddress.trim();

    if (!enteredAddress || enteredAddress !== destinationAddress) {
      alert('Paste the exact wallet address you sent the Pi to.');
      return;
    }

    const confirmed = window.confirm(
      `Confirm that ${Number(withdrawal.amount).toFixed(2)} Pi was sent to:\n\n` +
        `${withdrawal.walletAddress}\n\n` +
        'This will mark the withdrawal as completed.'
    );

    if (!confirmed) return;

    try {
      setProcessingAction('approve');

      await api.post(
        `/withdrawals/${id}/approve?network=${network}`,
        {
          confirmedWalletAddress: enteredAddress,
        }
      );

      navigate(`/admin/withdrawals?network=${network}`);
    } catch (err) {
      console.error('❌ Approve failed:', err);

      alert(
        err?.response?.data?.message ||
          'Failed to approve the withdrawal.'
      );
    } finally {
      setProcessingAction('');
    }
  };

  const handleReject = async () => {
    if (processingAction || !withdrawal) return;

    const confirmed = window.confirm(
      'Reject this withdrawal request?\n\n' +
        `User: @${withdrawal.username}\n` +
        `Amount: ${Number(withdrawal.amount).toFixed(2)} Pi\n` +
        `Destination: ${withdrawal.walletAddress}\n\n` +
        "The Pi will be returned to the user's BarterFi wallet."
    );

    if (!confirmed) return;

    try {
      setProcessingAction('reject');

      await api.post(
        `/withdrawals/${id}/reject?network=${network}`
      );

      navigate(`/admin/withdrawals?network=${network}`);
    } catch (err) {
      console.error('❌ Reject failed:', err);

      alert(
        err?.response?.data?.message ||
          'Failed to reject the withdrawal.'
      );
    } finally {
      setProcessingAction('');
    }
  };

  const handleCopy = async () => {
    if (!withdrawal?.walletAddress) return;

    try {
      await navigator.clipboard.writeText(withdrawal.walletAddress);
      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error('❌ Failed to copy wallet address:', err);
      alert('Failed to copy the wallet address.');
    }
  };

  if (!withdrawal) {
    return <p>Loading...</p>;
  }

  const isPending = withdrawal.status === 'pending';
  const isProcessing = Boolean(processingAction);

  return (
    <div className="withdrawal-detail">
      <div className="top-bar">
        <img
          src={backIcon}
          alt="Back"
          onClick={() =>
            navigate(`/admin/withdrawals?network=${network}`)
          }
        />

        <h2>
          Withdrawal Detail (
          {network === 'testnet' ? 'Testnet' : 'Mainnet'})
        </h2>
      </div>

      <div className="detail-content">
        <p>
          <strong>Username:</strong> {withdrawal.username}
        </p>

        <p>
          <strong>Amount:</strong>{' '}
          {Number(withdrawal.amount).toFixed(2)} Pi
        </p>

        <p>
          <strong>Status:</strong> {withdrawal.status}
        </p>

        <p>
          <strong>Requested At:</strong>{' '}
          {withdrawal.createdAt
            ? new Date(withdrawal.createdAt).toLocaleString()
            : ''}
        </p>

        {withdrawal.completedAt && (
          <p>
            <strong>Completed At:</strong>{' '}
            {new Date(withdrawal.completedAt).toLocaleString()}
          </p>
        )}

        {withdrawal.rejectedAt && (
          <p>
            <strong>Rejected At:</strong>{' '}
            {new Date(withdrawal.rejectedAt).toLocaleString()}
          </p>
        )}

        <p>
          <strong>Destination Wallet:</strong>{' '}
          {withdrawal.walletAddress}

          <button
            type="button"
            className="copy-btn"
            onClick={handleCopy}
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </p>

        {withdrawal.adminNote && (
          <p>
            <strong>Admin Note:</strong> {withdrawal.adminNote}
          </p>
        )}

        {withdrawal.txId && (
          <p>
            <strong>Tx:</strong> {withdrawal.txId}
          </p>
        )}

        {isPending && (
          <input
            type="text"
            placeholder="Paste wallet address you sent Pi to"
            value={confirmationAddress}
            onChange={(event) =>
              setConfirmationAddress(event.target.value)
            }
            className="confirm-address-input"
            disabled={isProcessing}
            autoComplete="off"
          />
        )}
      </div>

      {isPending && (
        <div className="fixed-footer">
          <button
            type="button"
            className="reject-btn"
            onClick={handleReject}
            disabled={isProcessing}
          >
            {processingAction === 'reject'
              ? 'Rejecting…'
              : 'Reject'}
          </button>

          <button
            type="button"
            className="approve-btn"
            onClick={handleApprove}
            disabled={isProcessing}
          >
            {processingAction === 'approve'
              ? 'Approving…'
              : 'Approve'}
          </button>
        </div>
      )}
    </div>
  );
}
